// BHP-1115: which labels a customer may edit, and what a save actually sends.
//
// `PATCH /api/vps/{uuid}` takes a PARTIAL label change: only the keys you want
// to change, `null` to remove one, and anything you leave out stays as it is.
// Sending the whole rendered set would be wrong in both directions — it cannot
// express a removal, and it drags along values the UI invented.
import type { VpsSummary } from './vpsSummary';

/**
 * Labels the platform sets and the backend refuses to let a client change
 * (BHP-1114, mirrored from `ServerLabel` in the API).
 *
 * A copy, because the frontend has no way to read the backend's enum. It only
 * decides what is hidden and what is sent; the actual guard lives server-side,
 * so the worst a stale copy here can do is offer a field that then comes back
 * 422 — never a change that slips through.
 *
 * The copy exists because these keys arrive mixed into `labels`. Promoting
 * `server-type` and `installation` to real fields on the DTO would remove the
 * need for it — see the follow-up noted on BHP-1115.
 */
export const RESERVED_LABELS = [
  'customerUuid',
  'server-type',
  'installation',
  'ManagedBy',
  'Client'
] as const;

/**
 * Case-insensitive, like the backend: Hetzner stores `managedby` next to
 * `ManagedBy` as a separate label, so a variant would render as editable and
 * then be refused.
 */
export function isReservedLabel(key: string): boolean {
  return RESERVED_LABELS.some(
    (reserved) => reserved.toLowerCase() === key.toLowerCase()
  );
}

export interface LabelRow {
  key: string;
  value: string;
}

/**
 * The rows a customer may edit: what the API returned, minus the platform's own
 * labels. Those are not rendered at all — they are bookkeeping the customer
 * never set and cannot change (BHP-1114), and `customerUuid` is an internal
 * identifier that has no business on their screen.
 */
export function editableLabels(labels: Record<string, string>): LabelRow[] {
  return Object.entries(labels)
    .filter(([key]) => !isReservedLabel(key))
    .map(([key, value]) => ({ key, value }));
}

/**
 * What is wrong with a row, keyed by its index. Empty when the form is fine.
 *
 * Checked before sending rather than after: `buildVpsEdit()` drops a reserved
 * key so it can never reach Hetzner, and the backend refuses it as well
 * (BHP-1114) — but a user who types one would get a successful save with their
 * label quietly gone. Two guards that both work and still leave the person
 * looking at a screen that lies.
 */
export function labelRowErrors(
  rows: readonly LabelRow[]
): Record<number, string> {
  const errors: Record<number, string> = {};
  const seen = new Map<string, number>();

  rows.forEach((row, index) => {
    const key = row.key.trim();

    if (key === '') {
      // An untouched empty row is just an unfinished thought; a value without a
      // name is a label the user believes they are adding.
      if (row.value.trim() !== '') {
        errors[index] = 'Give this label a name.';
      }

      return;
    }

    if (isReservedLabel(key)) {
      errors[index] = `"${key}" is set by the platform and cannot be changed.`;

      return;
    }

    // Hetzner keys are a map, so a duplicate silently overwrites: the user
    // would see two rows and get one label.
    const earlier = seen.get(key.toLowerCase());
    if (earlier !== undefined) {
      errors[index] = `"${key}" is already used above.`;

      return;
    }

    seen.set(key.toLowerCase(), index);
  });

  return errors;
}

/** Whether another blank row would just sit there unfilled. */
export function canAddLabelRow(rows: readonly LabelRow[]): boolean {
  return rows.every((row) => row.key.trim() !== '');
}

export interface VpsEdit {
  hostname?: string;
  labels?: Record<string, string | null>;
}

/**
 * Turn the form state into the smallest request that expresses it.
 *
 * Compared against `server.labels` — what the API returned — and never against
 * what the page renders. That distinction is the whole point of this function:
 * `serverTypeOf(server) ?? 'generic'` (BHP-1102) makes an unlabelled server
 * *look* like it has `server-type: generic`, and a form that echoed back what
 * it displayed would add a reserved label to a server that never had one. The
 * backend answers 422 for a key the user never touched.
 *
 * Reserved keys are dropped rather than diffed: they are not editable, so any
 * difference is our bug, not an intention. Unchanged rows are left out — an
 * empty patch is a request that says nothing.
 *
 * @param rows editable rows as they stand in the form
 */
export function buildVpsEdit(
  server: VpsSummary,
  hostname: string,
  rows: readonly LabelRow[]
): VpsEdit {
  const edit: VpsEdit = {};

  const trimmedHostname = hostname.trim();
  if (trimmedHostname !== '' && trimmedHostname !== server.hostname) {
    edit.hostname = trimmedHostname;
  }

  const labels = diffLabels(server, rows);
  if (Object.keys(labels).length > 0) {
    edit.labels = labels;
  }

  return edit;
}

function diffLabels(
  server: VpsSummary,
  rows: readonly LabelRow[]
): Record<string, string | null> {
  const changes: Record<string, string | null> = {};

  const kept = new Set<string>();
  for (const { key, value } of rows) {
    const trimmedKey = key.trim();
    // A half-typed row is not a change yet. Without this, adding a row would
    // immediately try to write an empty key.
    if (trimmedKey === '' || isReservedLabel(trimmedKey)) {
      continue;
    }

    kept.add(trimmedKey);

    if (server.labels[trimmedKey] !== value) {
      changes[trimmedKey] = value;
    }
  }

  // Removals: a key the API gave us that no row carries any more. Reserved keys
  // are never in `rows`, so they must be skipped explicitly or every save would
  // ask to delete them.
  for (const key of Object.keys(server.labels)) {
    if (!isReservedLabel(key) && !kept.has(key)) {
      changes[key] = null;
    }
  }

  return changes;
}

/** Nothing to send: the form matches what the server already has. */
export function isEmptyEdit(edit: VpsEdit): boolean {
  return edit.hostname === undefined && edit.labels === undefined;
}
